/*
 * EMEDEMORA · Armador de relojes de la ficha (estructura de Era Glasses).
 *
 * Lo pinta el bloque «Liquid personalizado» del armador en la plantilla de producto (lo genera
 * _herramientas/tema/armar_tema_dawn.py). Hace lo mismo que el prototipo 04-tienda/prototipo/ficha-era/:
 * - hasta 3 relojes del mismo modelo, cada uno con su color; la pieza en existencia se la queda el primero que la elige
 *   y el siguiente del mismo color sale sobre pedido;
 * - la fecha de llegada de cada uno (hábiles en existencia, naturales sobre pedido) y la insignia de la foto;
 * - la galería, el precio y la dirección siguen al reloj activo;
 * - agrega todo al carrito de una vez, con la entrega de cada reloj como propiedad de la línea («Entrega»).
 * Dos modos (data-modo):
 * - «familia»: cada color es su propio producto (Mario, 2026-10-18: «el Santos negro será otro producto»). Los colores
 *   son los productos de la misma familia (etiqueta familia-…). Al elegir otro, la página cambia a ese producto sin
 *   recargar, con el mismo cambio de producto que usa Dawn (product-info), y el armador nuevo hereda lo que llevabas.
 * - «colores»: un producto con un color por variante (los modelos que aún no se pasan). Mueve el selector de color de
 *   Dawn (oculto).
 * El ahorro por 2 y 3 relojes sale de data-descuentos (el % menos con 1, 2 y 3 relojes: los descuentos automáticos de
 * Shopify, 10 % y 20 % desde el 2026-10-19). Sin él, de data-ahorros (pesos fijos), que va en 0. Si se quitan los
 * descuentos en Shopify, aquí van en 0: si no, la página prometería un ahorro que el pago no hace.
 * Fechas (Mario, 2026-10-19): en existencia, 5 a 7 días hábiles; sobre pedido, 20 a 30 días naturales.
 * Escalones (ficha nueva, 2026-10-19): si el armador trae botones [data-emd-escalon] («Lleva 1, 2 o 3 piezas»), esos
 * botones dicen cuántos relojes van; cada uno sigue eligiendo su color abajo. El precio de cada escalón se recalcula con
 * el color activo.
 */
if (!customElements.get('emd-armador')) {
  const formato = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
  const sumarDias = (desde, dias, habiles) => {
    const f = new Date(desde);
    let quedan = dias;
    while (quedan > 0) {
      f.setDate(f.getDate() + 1);
      const d = f.getDay();
      if (!habiles || (d !== 0 && d !== 6)) quedan -= 1;
    }
    // la paquetería no entrega en fin de semana: se pasa al lunes
    while (f.getDay() === 0 || f.getDay() === 6) f.setDate(f.getDate() + 1);
    return f;
  };
  const fecha = (f) => formato.format(f).replace(',', '');
  // con centavos solo si los hay: el 10 % de $7,998 son $7,198.20, y es lo que cobra Shopify
  const dinero = (centavos) =>
    '$' + (centavos / 100).toLocaleString('es-MX', { minimumFractionDigits: centavos % 100 ? 2 : 0, maximumFractionDigits: 2 });
  const escapar = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  const suave = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
  const FLECHA = (dir, etiqueta) =>
    `<button type="button" class="emd-flecha emd-flecha--${dir < 0 ? 'izq' : 'der'}" data-mover="${dir}" aria-label="${etiqueta}">` +
    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${dir < 0 ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}"/></svg></button>`;

  class EmdArmador extends HTMLElement {
    connectedCallback() {
      if (this.iniciado) {
        if (this.relojes) this.escuchar();
        return;
      }
      this.iniciado = true;
      this.seccion = this.dataset.section;
      this.modo = this.dataset.modo === 'familia' ? 'familia' : 'colores';
      this.productoId = Number(this.dataset.productoId);
      this.maximo = Number(this.dataset.maximo) || 3;
      this.ahorros = (this.dataset.ahorros || '0,0,0').split(',').map((n) => Math.max(0, Number(n) || 0) * 100);
      this.descuentos = (this.dataset.descuentos || '').split(',').map((n) => Math.max(0, Math.min(90, Number(n) || 0)));
      this.variantes = JSON.parse(this.querySelector('[data-emd-variantes]').textContent).filter(
        (v) => v.disponible || v.existencia > 0
      );
      if (!this.variantes.length) return;

      const hoy = new Date();
      this.rango = {
        existencia: [sumarDias(hoy, 5, true), sumarDias(hoy, 7, true)].map(fecha),
        pedido: [sumarDias(hoy, 20, false), sumarDias(hoy, 30, false)].map(fecha),
      };
      const inicial = this.variante(Number(this.dataset.seleccionada)) || this.variantes[0];
      this.relojes = [inicial.id];
      this.activo = 0;
      // Viene de otro reloj de la familia: se queda con lo que ya llevabas, y el reloj activo es el de esta página
      const traspaso = EmdArmador.traspaso;
      EmdArmador.traspaso = null;
      if (traspaso && traspaso.relojes.length && traspaso.relojes.every((id) => this.variante(id))) {
        this.relojes = traspaso.relojes.slice(0, this.maximo);
        this.activo = Math.min(traspaso.activo, this.relojes.length - 1);
        this.relojes[this.activo] = inicial.id;
      }

      this.cuerpo = this.querySelector('.emd-a-cuerpo');
      this.cuerpo.hidden = false;
      this.escalones = [...this.querySelectorAll('[data-emd-escalon]')];
      if (this.escalones.length) this.classList.add('emd-armador--escalones');
      this.principal = document.getElementById(`MainProduct-${this.seccion}`);
      this.principal?.classList.add('emd-armador-listo');

      this.addEventListener('click', this.alClic.bind(this));
      // Las miniaturas de la galería eligen el color del reloj activo, como en Era
      document.getElementById(`GalleryThumbnails-${this.seccion}`)?.addEventListener('click', this.alMiniatura.bind(this));
      this.escuchar();
      this.pintar();
      if (traspaso) {
        // las pistas de colores y el foco quedan donde estaban antes del cambio de producto
        this.querySelectorAll('.emd-r-pista').forEach((x, i) => {
          if (traspaso.posiciones?.[i]) x.scrollLeft = traspaso.posiciones[i];
        });
        if (traspaso.enfocar) {
          this.querySelector(`.emd-muestra[data-i="${this.activo}"][data-v="${inicial.id}"]`)?.focus({ preventScroll: true });
        }
      }
    }

    // Lo que se escucha fuera del armador. El botón de la barra fija y el de Dawn mandan el formulario del producto:
    // aquí se agrega lo del armador
    escuchar() {
      if (this.alEnviarFn) return;
      this.alEnviarFn = this.alEnviar.bind(this);
      window.addEventListener('submit', this.alEnviarFn, true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.desuscribir = subscribe(PUB_SUB_EVENTS.variantChange, this.alCambiarVariante.bind(this));
      }
    }

    // Al cambiar de producto, Dawn deja la página vieja medio segundo antes de quitarla: sus escuchas se van con ella
    disconnectedCallback() {
      if (this.alEnviarFn) window.removeEventListener('submit', this.alEnviarFn, true);
      this.alEnviarFn = null;
      this.desuscribir?.();
      this.desuscribir = null;
    }

    variante(id) {
      return this.variantes.find((v) => v.id === id);
    }

    // La pieza en existencia es del primer reloj que la elige; el siguiente del mismo color sale sobre pedido
    estados() {
      const usados = {};
      return this.relojes.map((id) => {
        usados[id] = (usados[id] || 0) + 1;
        return usados[id] <= this.variante(id).existencia ? 'existencia' : 'pedido';
      });
    }

    quedaPara(i, id) {
      return this.variante(id).existencia - this.relojes.slice(0, i).filter((x) => x === id).length;
    }

    entre(clave) {
      return `entre el ${this.rango[clave][0]} y el ${this.rango[clave][1]}`;
    }

    muestra(i, v) {
      const elegido = this.relojes[i] === v.id;
      const agotado = v.existencia > 0 && this.quedaPara(i, v.id) <= 0;
      const estado = v.existencia > 0 && !agotado ? 'en existencia' : 'sobre pedido';
      return (
        `<button type="button" class="emd-muestra${elegido ? ' emd-sel' : ''}" data-i="${i}" data-v="${v.id}" aria-pressed="${elegido}" aria-label="${escapar(v.color)}, ${estado}">` +
        (v.foto ? `<img src="${v.foto}" alt="" width="42" height="52" loading="lazy">` : '') +
        `<span class="emd-m-nombre">${escapar(v.color)}</span>` +
        (agotado ? '<span class="emd-m-nota">sobre pedido</span>' : '') +
        '</button>'
      );
    }

    tarjeta(i, est) {
      const v = this.variante(this.relojes[i]);
      const n = this.relojes.length;
      const enExistencia = this.variantes.filter((x) => x.existencia > 0);
      const sobrePedido = this.variantes.filter((x) => x.existencia <= 0);
      const nombre = n === 1 && (this.escalones?.length || this.maximo === 1) ? 'Elige el color' : `Reloj ${i + 1}`;
      let html =
        `<div class="emd-reloj" data-i="${i}"><div class="emd-r-cabeza"><span class="emd-r-num">${nombre}</span>` +
        (n > 1 ? `<button type="button" class="emd-r-quitar" data-quitar="${i}">Quitar</button>` : '') +
        '</div>';
      if (enExistencia.length) {
        html +=
          '<div class="emd-r-grupo"><div class="emd-r-label"><span>En existencia · llega en 5 a 7 días hábiles</span>' +
          `<span class="emd-r-elegido">${v.existencia > 0 ? escapar(v.color) : ''}</span></div>` +
          `<div class="emd-r-muestras emd-r-tres">${enExistencia.map((x) => this.muestra(i, x)).join('')}</div></div>`;
      }
      if (sobrePedido.length) {
        const flechas = sobrePedido.length > 3;
        html +=
          '<div class="emd-r-grupo"><div class="emd-r-label"><span>Sobre pedido · llega en 20 a 30 días</span>' +
          `<span class="emd-r-elegido">${v.existencia > 0 ? '' : escapar(v.color)}</span></div>` +
          `<div class="emd-carrusel">${flechas ? FLECHA(-1, 'Ver colores anteriores') : ''}` +
          `<div class="emd-r-muestras emd-r-pista">${sobrePedido.map((x) => this.muestra(i, x)).join('')}</div>` +
          `${flechas ? FLECHA(1, 'Ver más colores') : ''}</div></div>`;
      }
      return (
        html +
        '<div class="emd-r-resumen">' +
        (v.foto ? `<img src="${v.foto}" alt="" width="35" height="44" loading="lazy">` : '') +
        `<span><b>${escapar(v.titulo || v.color)}</b>` +
        `<span class="emd-estado emd-estado--${est[i]}">${est[i] === 'existencia' ? 'En existencia' : 'Sobre pedido'}</span></span></div></div>`
      );
    }

    // Lo que se ahorra con n relojes que juntos valen `lista` (centavos), redondeado a pesos
    ahorroPara(n, lista) {
      const pct = this.descuentos[n - 1] || 0;
      if (pct) return Math.round((lista * pct) / 100);
      return Math.min(this.ahorros[n - 1] || 0, lista);
    }

    totales() {
      const n = this.relojes.length;
      const lista = this.relojes.reduce((suma, id) => suma + this.variante(id).precio, 0);
      const ahorro = this.ahorroPara(n, lista);
      return { n, lista, ahorro, total: lista - ahorro };
    }

    accion(est) {
      const { n, ahorro } = this.totales();
      if (n > 1) return ahorro ? 'Agregar y ahorrar' : 'Agregar al carrito';
      return est[0] === 'pedido' ? 'Encargar el mío' : 'Agregar al carrito';
    }

    pintar() {
      const est = this.estados();
      const { n, lista, ahorro, total } = this.totales();
      const v = this.variante(this.relojes[this.activo]);
      const $ = (s) => this.querySelector(s);

      let l1;
      let l2;
      if (n === 1) {
        const a2 = this.ahorroPara(2, 2 * v.precio);
        l1 = 'Agrega un segundo reloj' + (a2 ? ` <b class="emd-acento">AHORRA ${dinero(a2)}</b>` : '');
        l2 = a2 ? `Lleva 2 por ${dinero(2 * v.precio - a2)}` : 'Uno para ti y otro para regalar';
      } else {
        const a3 = n === 2 ? this.ahorroPara(3, lista + v.precio) : 0;
        l1 = `${n} relojes` + (ahorro ? ` · <b class="emd-acento">AHORRAS ${dinero(ahorro)}</b>` : '');
        l2 = n < this.maximo ? (a3 ? `Agrega un tercero y ahorra ${dinero(a3)} en total` : 'Puedes agregar uno más') : 'Es el máximo por pedido';
      }
      $('.emd-a-l1').innerHTML = l1;
      $('.emd-a-l2').innerHTML = l2;
      $('.emd-a-der').innerHTML =
        `<div class="emd-a-precio">${dinero(total)} MXN${ahorro ? ` <s>${dinero(lista)}</s>` : ''}</div>` +
        (ahorro ? `<div class="emd-a-ahorro">Ahorras ${dinero(ahorro)} MXN</div>` : '');

      const posiciones = [...this.querySelectorAll('.emd-r-pista')].map((x) => x.scrollLeft);
      $('.emd-relojes').innerHTML = this.relojes.map((_, i) => this.tarjeta(i, est)).join('');
      this.querySelectorAll('.emd-r-pista').forEach((x, i) => {
        if (posiciones[i]) x.scrollLeft = posiciones[i];
      });
      $('.emd-anadir').hidden = n >= this.maximo;
      $('.emd-seleccion').innerHTML =
        'Tu selección: ' +
        this.relojes
          .map((id, i) => `<b>Reloj ${i + 1}:</b> ${escapar(this.variante(id).color)} (${est[i] === 'existencia' ? 'en existencia' : 'sobre pedido'})`)
          .join(' | ');
      const accion = this.accion(est);
      // «MXN» va aparte: en la ficha nueva, en celular, se esconde para que el botón quepa en un renglón (todo en un solo
      // span, porque el botón es flex y cada pedazo suelto sería otra pieza con su hueco)
      $('.emd-agregar').innerHTML = `<span>${accion} – ${dinero(total)}<span class="emd-agregar-moneda"> MXN</span></span>`;

      // Los escalones: el de ahora marcado, y el precio de cada uno con el color activo
      if (this.escalones?.length) {
        this.escalones.forEach((b) => {
          const k = Number(b.dataset.emdEscalon);
          const elegido = k === n;
          b.classList.toggle('is-activo', elegido);
          b.setAttribute('aria-checked', String(elegido));
          const listaK = elegido ? lista : k * v.precio;
          const ahorroK = elegido ? ahorro : this.ahorroPara(k, listaK);
          const precio = b.querySelector('[data-emd-e-precio]');
          if (precio) precio.textContent = dinero(listaK - ahorroK);
          const antes = b.querySelector('[data-emd-e-antes]');
          if (antes) antes.textContent = ahorroK ? dinero(listaK) : '';
        });
      }

      // La fecha de llegada, arriba del armador
      const hay = new Set(est);
      let entrega;
      if (hay.size > 1) {
        entrega = `Lo que está en existencia llega <u>${this.entre('existencia')}</u>. Lo sobre pedido se arma para ti y llega <u>${this.entre('pedido')}</u>.`;
      } else if (hay.has('pedido')) {
        entrega = `Se arma${n > 1 ? 'n' : ''} para ti desde cero: haz tu pedido hoy y llega${n > 1 ? 'n' : ''} <u>${this.entre('pedido')}</u>.`;
      } else {
        entrega = `Haz tu pedido hoy para recibir${n > 1 ? 'los' : 'lo'} <u>${this.entre('existencia')}</u>.`;
      }
      this.principal?.querySelectorAll('[data-emd-entrega-texto]').forEach((el) => {
        el.innerHTML = entrega;
      });

      this.pintarFuera();
    }

    // Lo que vive fuera del armador y Dawn redibuja al cambiar de color: la insignia de la foto y la barra fija
    pintarFuera() {
      const est = this.estados();
      const { lista, ahorro, total } = this.totales();
      const envoltura = this.principal?.querySelector('.product__media-wrapper');
      if (envoltura) {
        let insignia = envoltura.querySelector('.emd-insignia');
        if (!insignia) {
          insignia = document.createElement('span');
          insignia.className = 'emd-insignia';
          envoltura.prepend(insignia);
        }
        const e = est[this.activo];
        insignia.textContent = e === 'existencia' ? 'En existencia' : 'Sobre pedido';
        insignia.classList.toggle('emd-insignia--pedido', e === 'pedido');
      }
      const boton = document.querySelector(`#StickyAtcSubmitButton-${this.seccion} > span`);
      if (boton) boton.textContent = this.accion(est);
      const precio = document.getElementById(`atc-price-${this.seccion}`);
      if (precio) {
        precio.innerHTML = `<span class="emd-b-precio">${dinero(total)} MXN${ahorro ? ` <s>${dinero(lista)}</s>` : ''}</span>`;
      }
    }

    // La galería, el precio y la dirección siguen al reloj activo: en «familia» cambia de producto; en «colores», el
    // selector de color de Dawn (?variant=)
    seguirConDawn(enfocar = false) {
      const v = this.variante(this.relojes[this.activo]);
      if (!v) return;
      if (this.modo === 'familia') {
        if (v.producto !== this.productoId && v.url) this.irAProducto(v.url, enfocar);
        return;
      }
      const selector = document.getElementById(`variant-selects-${this.seccion}`);
      if (!selector) return;
      const radio = [...selector.querySelectorAll('input[type="radio"]')].find((x) => x.value === v.color);
      if (radio) {
        if (radio.checked) return;
        this.esperando = v.id;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const lista = selector.querySelector('select');
      if (lista && lista.value !== v.color) {
        this.esperando = v.id;
        lista.value = v.color;
        lista.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Cambia la página al producto del reloj activo, como cuando Dawn cambia de producto en una lista combinada
    irAProducto(url, enfocar) {
      EmdArmador.traspaso = {
        relojes: [...this.relojes],
        activo: this.activo,
        enfocar,
        posiciones: [...this.querySelectorAll('.emd-r-pista')].map((x) => x.scrollLeft),
      };
      // Con una plantilla de vista previa (?view=nueva), el otro producto se pide con la misma y la dirección la conserva:
      // si no, el cambio de color traería la ficha publicada
      EmdArmador.vista ??= new URLSearchParams(window.location.search).get('view') || '';
      const vista = EmdArmador.vista;
      const pedir = vista ? `${url}${url.includes('?') ? '&' : '?'}view=${encodeURIComponent(vista)}` : url;
      const info = this.closest('product-info');
      if (info && typeof info.renderProductInfo === 'function' && typeof info.handleSwapProduct === 'function') {
        const cambiar = info.handleSwapProduct(url, true);
        info.renderProductInfo({
          requestUrl: pedir,
          targetId: this.id,
          callback: (html) => {
            cambiar(html);
            if (!vista) return;
            const direccion = new URL(window.location.href);
            direccion.searchParams.set('view', vista);
            window.history.replaceState({}, '', direccion);
          },
        });
      } else {
        window.location.assign(pedir);
      }
    }

    elegir(i, id, enfocar = false) {
      this.relojes[i] = id;
      this.activo = i;
      this.pintar();
      // el armador se vuelve a pintar entero: el foco regresa a la muestra elegida
      if (enfocar) this.querySelector(`.emd-muestra[data-i="${i}"][data-v="${id}"]`)?.focus({ preventScroll: true });
      this.seguirConDawn(enfocar);
    }

    // Cuántos relojes van (los escalones): los que se agregan toman un color libre en existencia del mismo modelo, como
    // «Añadir otro reloj»; si no queda, repiten el color activo y salen sobre pedido
    ponerCantidad(cantidad) {
      const n = Math.max(1, Math.min(this.maximo, cantidad || 1));
      while (this.relojes.length < n) {
        const libre = this.variantes.find((v) => v.existencia > 0 && !this.relojes.includes(v.id));
        this.relojes.push(libre ? libre.id : this.relojes[this.activo]);
      }
      this.relojes.length = n;
      this.activo = Math.min(this.activo, n - 1);
      this.pintar();
      this.seguirConDawn();
    }

    alClic(ev) {
      const e = ev.target.closest('[data-emd-escalon]');
      if (e) return this.ponerCantidad(Number(e.dataset.emdEscalon));
      const m = ev.target.closest('.emd-muestra');
      if (m) return this.elegir(Number(m.dataset.i), Number(m.dataset.v), true);
      const q = ev.target.closest('[data-quitar]');
      if (q) {
        this.relojes.splice(Number(q.dataset.quitar), 1);
        this.activo = Math.min(this.activo, this.relojes.length - 1);
        this.pintar();
        return this.seguirConDawn();
      }
      const f = ev.target.closest('[data-mover]');
      if (f) {
        const pista = f.parentElement.querySelector('.emd-r-pista');
        if (pista) pista.scrollBy({ left: Number(f.dataset.mover) * pista.clientWidth * 0.67, behavior: suave() });
        return;
      }
      if (ev.target.closest('.emd-anadir')) {
        if (this.relojes.length >= this.maximo) return;
        const libre = this.variantes.find((v) => v.existencia > 0 && !this.relojes.includes(v.id));
        this.relojes.push(libre ? libre.id : this.relojes[this.relojes.length - 1]);
        this.activo = this.relojes.length - 1;
        this.pintar();
        return this.seguirConDawn();
      }
      if (ev.target.closest('.emd-agregar')) this.agregar();
    }

    alMiniatura(ev) {
      if (this.modo === 'familia') return;
      const item = ev.target.closest('[data-target]');
      if (!item) return;
      const media = Number(String(item.dataset.target).split('-').pop());
      const v = this.variantes.find((x) => x.media === media);
      if (v && v.id !== this.relojes[this.activo]) this.elegir(this.activo, v.id);
    }

    alCambiarVariante({ data } = {}) {
      if (!data || String(data.sectionId) !== String(this.seccion) || !data.variant) return;
      const id = data.variant.id;
      if (this.esperando === id) this.esperando = null;
      else if (this.variante(id) && id !== this.relojes[this.activo]) {
        // alguien movió el selector de Dawn (o la barra fija): el reloj activo toma ese color
        this.relojes[this.activo] = id;
        this.pintar();
        return;
      }
      this.pintarFuera();
    }

    alEnviar(ev) {
      const form = ev.target;
      if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== `product-form-${this.seccion}`) return;
      if (!this.isConnected || this.closest('main')?.style.display === 'none') return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      this.agregar();
    }

    lineas() {
      const est = this.estados();
      const lineas = [];
      this.relojes.forEach((id, i) => {
        const Entrega =
          est[i] === 'existencia'
            ? `En existencia · llega ${this.entre('existencia')}`
            : `Sobre pedido · se arma para ti y llega ${this.entre('pedido')}`;
        const igual = lineas.find((l) => l.id === id && l.properties.Entrega === Entrega);
        if (igual) igual.quantity += 1;
        else lineas.push({ id, quantity: 1, properties: { Entrega } });
      });
      return lineas;
    }

    async agregar() {
      if (this.ocupado) return;
      this.ocupado = true;
      const botones = [this.querySelector('.emd-agregar'), document.getElementById(`StickyAtcSubmitButton-${this.seccion}`)].filter(Boolean);
      botones.forEach((b) => b.classList.add('loading'));
      const error = this.querySelector('.emd-a-error');
      error.hidden = true;

      const carrito = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const pedido = { items: this.lineas() };
      if (carrito) {
        pedido.sections = carrito.getSectionsToRender().map((s) => s.id);
        pedido.sections_url = window.location.pathname;
        carrito.setActiveElement?.(document.activeElement);
      }
      try {
        const raiz = window.Shopify?.routes?.root || '/';
        const r = await fetch(`${raiz}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(pedido),
        });
        const datos = await r.json();
        if (!r.ok || datos.status) {
          error.textContent = datos.description || datos.message || 'No se pudo agregar. Inténtalo de nuevo.';
          error.hidden = false;
          return;
        }
        if (!carrito) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'emd-armador', cartData: datos });
        }
        carrito.classList.remove('is-empty');
        carrito.renderContents(datos);
      } catch (e) {
        error.textContent = 'No se pudo agregar. Revisa tu conexión e inténtalo de nuevo.';
        error.hidden = false;
      } finally {
        this.ocupado = false;
        botones.forEach((b) => b.classList.remove('loading'));
      }
    }
  }
  customElements.define('emd-armador', EmdArmador);

  // «Elige el tuyo» y los demás enlaces a #emd-comprar bajan sin brincar
  document.addEventListener('click', (ev) => {
    const a = ev.target.closest('a[href="#emd-comprar"]');
    const destino = a && document.getElementById('emd-comprar');
    if (!destino) return;
    ev.preventDefault();
    destino.scrollIntoView({ behavior: suave(), block: 'start' });
  });
}
